import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getChainExplorerUrl } from '@/utils/chainExplorers';
import { routes } from '@/utils/routes';
import { Button, Flex, Heading, Text } from '@chakra-ui/react';
import { formatAddress } from '@onbeam/utils';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { rem } from 'polished';
import { TransferSuccessIcon } from './TransferSuccessIcon';

type Props = {
  tokenName: string;
  quantity: number;
  ownedQuantity: number;
  toAddress: string;
  onClose(): void;
  onReset(): void;
  transactionHash?: string | null;
};

export const TransferSuccess = ({
  tokenName,
  quantity,
  ownedQuantity,
  toAddress,
  onClose,
  onReset,
  transactionHash,
}: Props) => {
  const { t } = useTranslation('common');
  const marketplaceChain = useMarketplaceChain();

  return (
    <Flex flexDir="column" gap="space.32" p="space.32">
      <Heading fontSize="2xl" fontWeight="normal">
        {t('transfer-modal.success.title')}
      </Heading>
      <Flex align="center" flexDir="column" gap="space.12">
        <Text color="gray.6">
          {t('transfer-modal.success.description', { count: quantity, tokenName })}
          <Text
            as={Link}
            href={routes.profile(toAddress, marketplaceChain.routePrefix)}
            color="white"
            display="inline"
            fontWeight="medium"
            transition="opacity 120ms ease-in-out"
            _hover={{ opacity: 0.4 }}
          >
            {formatAddress(toAddress)}
          </Text>
          .
        </Text>
        {transactionHash && (
          <Text
            as={Link}
            href={getChainExplorerUrl(marketplaceChain.id, transactionHash, 'tx')}
            target="_blank"
            rel="noopener noreferrer"
            fontWeight="medium"
            transition="opacity 120ms ease-in-out"
            _hover={{ opacity: 0.4 }}
          >
            {t('transfer-modal.success.cta.chain-explorer')}
          </Text>
        )}
      </Flex>
      <TransferSuccessIcon width={rem(196)} height={rem(156)} mx="auto" />
      <Flex w="full" gap="space.24">
        {quantity < ownedQuantity && (
          <Button onClick={onReset} w="full" variant="secondary">
            {t('transfer-modal.success.cta.transfer')}
          </Button>
        )}
        <Button onClick={onClose} w="full">
          {t('transfer-modal.success.cta.close')}
        </Button>
      </Flex>
    </Flex>
  );
};
